import { useEffect, useState } from "react";
import { Bell, CheckCircle2, Phone, Users, Calendar } from "lucide-react";
import api, { fmtNaira, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";

export default function Debtors() {
  const { user } = useAuth();
  const t = useT(user?.preferred_language);
  const [data, setData] = useState({ debtors: [], raw: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/debtors");
      setData(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Iter34: live-refresh whenever a transaction is created or a debt gets
  // marked paid — either from WhatsApp, the mobile app, or another browser tab.
  useEffect(() => {
    const onLive = (e) => {
      const kind = e?.detail?.kind;
      if (kind === "transaction" || kind === "brain_sync") load();
    };
    window.addEventListener("kobolog:live", onLive);
    return () => window.removeEventListener("kobolog:live", onLive);
  }, []);

  const sendReminder = async (debtId) => {
    setBusy(debtId);
    try {
      const r = await api.post(`/debtors/${debtId}/remind`);
      toast.success(r.data.message);
    } catch (e) {
      toast.error("Failed to send reminder");
    } finally { setBusy(null); }
  };

  const markPaid = async (debtId) => {
    setBusy(debtId);
    try {
      await api.post(`/debtors/${debtId}/mark-paid`, {});
      toast.success("Marked as paid 🎉");
      await load();
    } catch (e) {
      toast.error("Failed");
    } finally { setBusy(null); }
  };

  if (loading) return <div className="p-6 text-center text-kudi-ink/50">{t("loadingDebtors")}</div>;

  const totalOwed = data.raw.reduce((s, d) => s + d.amount, 0);

  return (
    <div className="px-4 py-5 space-y-5 pb-6">
      <div>
        <div className="text-xs text-kudi-ink/55 uppercase tracking-wider font-semibold">{t("debtorsSubtitle")}</div>
        <h1 className="font-heading text-2xl font-bold mt-1">{t("debtorsTitle")}</h1>
      </div>

      {/* Total card */}
      <div className="bg-white border border-border rounded-2xl p-5 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-accent text-kudi-green flex items-center justify-center">
          <Users className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="text-xs text-kudi-ink/55">{t("totalOutstanding")}</div>
          <div className="font-heading text-2xl font-bold tabular-nums text-kudi-green" data-testid="total-owed">{fmtNaira(totalOwed)}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-kudi-ink/55">{t("debtorsCount")}</div>
          <div className="font-heading text-2xl font-bold">{data.debtors.length}</div>
        </div>
      </div>

      {/* Debtor list */}
      {data.debtors.length === 0 ? (
        <div className="bg-white border border-border rounded-2xl p-8 text-center" data-testid="no-debtors">
          <div className="w-14 h-14 rounded-2xl bg-accent text-kudi-green flex items-center justify-center mx-auto mb-3">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <div className="font-heading font-semibold">{t("everyonePaid")}</div>
          <p className="text-sm text-kudi-ink/60 mt-1">{t("noOneOwes")}</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="debtor-list">
          {data.debtors.map((d) => (
            <div key={d.name} className="bg-white border border-border rounded-2xl overflow-hidden">
              <div className="p-4 flex items-center gap-3 border-b border-border/60">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-kudi-terracotta to-kudi-green text-white flex items-center justify-center font-heading font-bold">
                  {d.name[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-semibold truncate">{d.name}</div>
                  <div className="text-xs text-kudi-ink/55">{d.items.length} {d.items.length > 1 ? t("unpaidItems") : t("unpaidItem")}</div>
                </div>
                <div className="text-right">
                  <div className="font-heading text-lg font-bold tabular-nums text-kudi-green">{fmtNaira(d.total)}</div>
                </div>
              </div>
              <div className="divide-y divide-border/40">
                {d.items.map((it) => (
                  <div key={it.id} className="px-4 py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{it.description || t("goodsOnCredit")}</div>
                      <div className="text-xs text-kudi-ink/50 mt-0.5 flex items-center gap-2">
                        <Calendar className="w-3 h-3" />
                        {it.due_date ? `${t("due")} ${fmtDate(it.due_date)}` : `${t("logged")} ${fmtDate(it.created_at)}`}
                      </div>
                    </div>
                    <div className="text-sm font-semibold tabular-nums">{fmtNaira(it.amount)}</div>
                  </div>
                ))}
              </div>
              <div className="px-3 py-3 bg-accent/40 flex gap-2">
                <button
                  onClick={() => sendReminder(d.items[0].id)}
                  disabled={busy === d.items[0].id}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-white border border-kudi-green/40 text-kudi-green rounded-full py-2 text-sm font-semibold hover:bg-kudi-green/5 transition disabled:opacity-50"
                  data-testid={`remind-btn-${d.name}`}
                >
                  <Bell className="w-4 h-4" />
                  {t("remind")}
                </button>
                <button
                  onClick={() => markPaid(d.items[0].id)}
                  disabled={busy === d.items[0].id}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-kudi-green text-white rounded-full py-2 text-sm font-semibold hover:bg-kudi-green-dark transition disabled:opacity-50"
                  data-testid={`paid-btn-${d.name}`}
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {t("markPaid")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
